/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });

    this.querySelectorAll('[data-tab-target]').forEach(tabButton => {
      tabButton.addEventListener('click', this.handleTabClick.bind(this))
    })
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  handleTabClick(event) {
    const tabButton = event.target.closest('[data-tab-target]')
    if (!tabButton) return

    const targetTab = this.querySelector(`[data-cart-tab="${tabButton.dataset.tabTarget}"]`)

    const tabButtons = this.querySelectorAll('[data-tab-target]')
    const tabs = this.querySelectorAll('[data-cart-tab]')

    tabButton.classList.add('cart-drawer__tab-title--active')
    targetTab.ariaHidden = false

    tabButtons.forEach(el => {
      if (el === tabButton) return

      el.classList.remove('cart-drawer__tab-title--active')
    })
    tabs.forEach(tab => {
      if (tab === targetTab) return

      tab.ariaHidden = true
    })
  }
}

window.customElements.define('cart-drawer', CartDrawer);

class CartWishlistTab extends HTMLElement {
  connectedCallback() {
    this._wishlistProducts = []

    // Check if window._swat exists, if not, set up a polling mechanism
    if (window._swat) {
      this.buildWishlist()
    } else {
      this.waitForSwat()
    }

    this.setupListeners()
  }

  waitForSwat() {
    const checkInterval = setInterval(() => {
      if (window._swat) {
        clearInterval(checkInterval)
        this.buildWishlist()
      }
    }, 100) // Check every 100ms
  }

  setupListeners() {
    document.addEventListener('dispatch:wishlist:update', this.buildWishlist.bind(this))
  }

  async buildWishlist() {
    this.wishlistProducts = await this.getWishlistProducts()

    if (!this.wishlistProducts) return

    this.renderWishlistItems(this.wishlistProducts)
  }

  async getWishlistProducts() {
    return new Promise((resolve, reject) => {
      window._swat.fetch(function(usersWishlist) {
        resolve(usersWishlist);
      }, function(error) {
        console.log("There was an error while fetching the wishlist", error);
        reject(error);
      });
    });
  }

  renderWishlistItems(data) {
    const el = document.createElement('div')

    data.forEach(item => {
      el.innerHTML += this.renderWishlistItem(item)
    })

    this.innerHTML = el.innerHTML
  }

  renderWishlistItem(itemJson) {
    const { empi: productId, epi: variantId, iu: imageSrc, dt: productTitle, pr: productPrice, du: productUrl, vi: size } = itemJson
    return `
      <cart-wishlist-item class="cart-wishlist-item cart-item" data-product-id="${productId}" data-variant-id="${variantId}" data-product-url="${productUrl}">
        <div class="cart-wishlist-item__image cart-item__column cart-item__image">
          <img src="${imageSrc}" />
        </div>
        <div class="cart-wishlist-item__content cart-item__not-image">
          <div class="cart-wishlist-item__header cart-item__column cart-item__description">
            <span class="cart-wishlist-item__title">${productTitle}</span>
          </div>

          <button class="cart-wishlist-item__remove cart-item__column cart-item__remove" data-remove-from-wishlist>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="{{ stroke_width | default: 1.5 }}" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-x" aria-hidden="true" focusable="false" role="presentation"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
          
          <div class="cart-wishlist-item__row cart-item__column cart-item__wishlist-price">
            <span class="cart-wishlist-item__price theme-money">$${productPrice}</span>
          </div>

          <div class="cart-wishlist-item__row cart-item__column cart-item__wishlist-options">
            <span class="cart-wishlist-item__size">Size: ${size}</span>
          </div>
          
          <div class="cart-wishlist-item__row cart-item__column cart-item__wishlist-move">
            <button class="btn cart-wishlist-item__cartButton" type="submit" data-move-to-cart>Move to cart</button>
          </div>
        </div>
      </cart-wishlist-item>
    `
  }

  get wishlistProducts() {
    return this._wishlistProducts
  }

  set wishlistProducts(data) {
    this._wishlistProducts = data
  }
}

window.customElements.define('cart-wishlist-tab', CartWishlistTab)

class CartWishlistItem extends HTMLElement {
  connectedCallback() {
    this.setupListeners()
  }

  setupListeners() {
    this.moveToCartButton?.addEventListener('click', this.moveToCart.bind(this))
    this.removeFromWishlistButton?.addEventListener('click', this.removeFromWishlist.bind(this))
  }

  async moveToCart(event) {
    this.addItemToCart()

    this.removeFromWishlist()
  }

  addItemToCart() {
    const formData = {
      'items': [{
        'id': this.variantId,
        'quantity': 1
      }]
    }
    fetch(window.theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error.message)
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }))
      })
  }

  async removeFromWishlist() {
    let wishlistProduct = {
      epi: Number(this.variantId),
      empi: Number(this.productId),
      du: this.productUrl
    }

    window._swat.removeFromWishList(wishlistProduct, function(wishlistProducts) {
      console.log(wishlistProducts)
      document.dispatchEvent(new CustomEvent('dispatch:wishlist:update'))
    }, function(error) {
      console.log("There was an error while removing product from the wishlist", error)
    })
  }

  get variantId() {
    return this.dataset.variantId
  }

  get productId() {
    return this.dataset.productId
  }

  get productUrl() {
    return this.dataset.productUrl
  }

  get moveToCartButton() {
    return this.querySelector('[data-move-to-cart]')
  }

  get removeFromWishlistButton() {
    return this.querySelector('[data-remove-from-wishlist]')
  }
}

window.customElements.define('cart-wishlist-item', CartWishlistItem)

class CartGWP extends HTMLElement {
  constructor() {
    super()

    this.handleCartChange = this.handleCartChange.bind(this)
  }
  connectedCallback() {
    document.addEventListener('on:cart:after-merge', this.handleCartChange)
  }

  disconnectedCallback() {
    document.removeEventListener('on:cart:after-merge', this.handleCartChange)
  }

  handleCartChange() {
    console.log('handleCartChange')
    if (this.shouldRemoveGift('regular_gift')) {
      this.removeItem('regular_gift')
    }

    if (this.shouldRemoveGift('luxury_gift')) {
      console.log('the luxury gift should be removed')
      this.removeItem('luxury_gift')
    }
  }

  removeItem(gift_handle) {
    const updates = {
      [this.gwpData[gift_handle].in_cart]: 0,
    }
    fetch(window.theme.routes.cartUpdate, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error.message)
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }))
      })
  }

  shouldRemoveGift(gift_handle) {
    const gift = this.gwpData[gift_handle]

    if (!gift.in_cart) return false

    if (!gift.enabled || this.cartTotal < gift.threshold) return true
  }

  get gwpData() {
    return JSON.parse(atob(this.dataset.gwpData))
  }

  get cartTotal() {
    return this.gwpData.cart_total
  }
}

window.customElements.define('gwp-progress-bar', CartGWP)
